// Polish working-day calendar — weekends plus the statutory public holidays
// (dni ustawowo wolne od pracy, Dz.U. 2015 poz. 90 with later amendments).
// Plain JS, no dayjs, so both the FE and edge functions (Deno) can use it.
//
// Dates in and out are "YYYY-MM-DD" and every calculation runs in UTC, matching
// the `new Date().toISOString().slice(0, 10)` convention the queue presets use.

const DAY_MS = 86_400_000;

/** Christmas Eve became statutory in Poland from 2025 (Dz.U. 2024 poz. 1809). */
const CHRISTMAS_EVE_STATUTORY_FROM = 2025;

/** Fixed-date holidays, "MM-DD". */
const FIXED = [
  "01-01", // Nowy Rok
  "01-06", // Trzech Króli
  "05-01", // Święto Pracy
  "05-03", // Święto Konstytucji 3 Maja
  "08-15", // Wniebowzięcie NMP
  "11-01", // Wszystkich Świętych
  "11-11", // Święto Niepodległości
  "12-25", // Boże Narodzenie
  "12-26", // drugi dzień świąt
];

const iso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const parse = (date: string): number => Date.parse(date.slice(0, 10) + "T00:00:00.000Z");

/**
 * Gregorian Easter Sunday for `year`, as "YYYY-MM-DD".
 * Anonymous Gregorian algorithm (Meeus/Jones/Butcher).
 */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return iso(Date.UTC(year, month - 1, day));
}

const cache = new Map<number, Set<string>>();

/** Polish statutory non-working days in `year`, as "YYYY-MM-DD" strings. */
export function polishHolidays(year: number): Set<string> {
  const hit = cache.get(year);
  if (hit) return hit;

  const days = new Set(FIXED.map((md) => `${year}-${md}`));
  if (year >= CHRISTMAS_EVE_STATUTORY_FROM) days.add(`${year}-12-24`); // Wigilia

  // Easter Sunday plus the three days anchored to it: Poniedziałek Wielkanocny
  // (+1), Zielone Świątki (+49) and Boże Ciało (+60).
  const easter = parse(easterSunday(year));
  for (const offset of [0, 1, 49, 60]) days.add(iso(easter + offset * DAY_MS));

  cache.set(year, days);
  return days;
}

/** True when `date` is neither a weekend nor a Polish statutory holiday. */
export function isWorkingDay(date: string): boolean {
  const day = date.slice(0, 10);
  const dow = new Date(parse(day)).getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return !polishHolidays(Number(day.slice(0, 4))).has(day);
}

/**
 * The first working day strictly after `date`. Friday returns Monday; the
 * Thursday before a Friday holiday returns Monday too.
 */
export function nextWorkingDay(date: string): string {
  // The longest Polish non-working run is the Christmas cluster (24–26.12 plus
  // an adjacent weekend, 5 days); 10 probes leaves ample headroom.
  let ms = parse(date) + DAY_MS;
  for (let probe = 0; probe < 10; probe++) {
    const day = iso(ms);
    if (isWorkingDay(day)) return day;
    ms += DAY_MS;
  }
  return iso(ms);
}

/**
 * Count working days `d` with `from < d <= to` — half-open on the left,
 * inclusive on the right. `from == to` (or `to` before `from`) is 0.
 *
 * Used by the documents-reminder gates: "unload Friday → first eligible send
 * Tuesday" is `workingDaysBetween(fridayUnload, tuesday) === 2`; "last send
 * Monday → next eligible Thursday" is `workingDaysBetween(mondaySend, thursday) === 3`.
 */
export function workingDaysBetween(from: string, to: string): number {
  const toMs = parse(to);
  let ms = parse(from) + DAY_MS;
  let count = 0;
  while (ms <= toMs) {
    if (isWorkingDay(iso(ms))) count++;
    ms += DAY_MS;
  }
  return count;
}
